<script setup lang="ts">
/**
 * 下单完成页（出取餐号 + 电子小票二维码）。
 *
 * ============================================================
 * 业务规则（2026-10-13 定）
 * ============================================================
 * **不用纸质小票取餐，凭付完款生成的电子小票取餐。**
 * 所以这一屏的任务不只是「告诉他号码」，而是**把凭据交到顾客手里**：
 *   · 取餐号用整屏最大的字（店员核对的就是这个号）；
 *   · 旁边出一个二维码，顾客扫一下就把电子小票存进手机 —— 走到取餐口能给店员看，
 *     离开这台触屏机也还有凭据（以前 15 秒就跳走，顾客手上什么都没有）；
 *   · 停留时间跟着「顾客有没有在扫码」走：一碰屏幕就重新计时，扫完按「完成」再回首页；
 *   · 语音播报（能播就播，播不了也不影响流程，屏幕才是准的）；
 *   · 测试收款必须明确写在屏幕上，不能让顾客以为付过了真钱。
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { kiosk } from '../stores/kiosk';
import { pickupAnnouncement, speak } from '../utils/voice';
import { buildReceiptUrl } from '../utils/receipt';
import { renderQrSvg } from '../utils/qr';

const router = useRouter();
const left = ref(kiosk.state.config.doneAutoBackSeconds);
const spoke = ref(false);
let timer: ReturnType<typeof setInterval> | null = null;

const order = computed(() => kiosk.state.order);
const pickup = computed(() => order.value?.pickupNo ?? order.value?.orderNo ?? '—');
const isTest = computed(() => order.value?.isTestMode === true);

/** 这张电子小票的链接（带上顾客自己的凭据） */
const receiptUrl = computed(() => {
  if (!order.value) return '';
  return buildReceiptUrl({
    origin: window.location.origin,
    pathname: window.location.pathname,
    orderNo: order.value.orderNo,
    credential: kiosk.state.session.customerOpenId,
  });
});

/** 二维码 SVG。生成失败不影响出餐：屏幕上仍有取餐号 */
const qrSvg = computed(() => {
  if (!receiptUrl.value) return '';
  try {
    return renderQrSvg(receiptUrl.value, 6, 8);
  } catch {
    return '';
  }
});

function backHome(): void {
  kiosk.resetToIdle();
  void router.push('/');
}

/** 顾客一碰屏幕就重新计时：正在扫码时不该在背后偷偷跳走 */
function keepAlive(): void {
  left.value = kiosk.state.config.doneAutoBackSeconds;
}

onMounted(() => {
  if (!order.value) {
    void router.replace('/');
    return;
  }

  // 播报两次：店里吵，一次经常听不到。中间隔 2.5 秒，不要连成一串糊在一起。
  if (!spoke.value) {
    spoke.value = true;
    const text = pickupAnnouncement(order.value.pickupNo, order.value.orderNo);
    speak(text);
    setTimeout(() => speak(text), 2500);
    // 再补一次，防止浏览器在页面刚加载时把语音吞掉
    setTimeout(() => speak(text), 5200);
  }

  timer = setInterval(() => {
    left.value -= 1;
    if (left.value <= 0) backHome();
  }, 1000);
});

onBeforeUnmount(() => {
  if (timer) clearInterval(timer);
});
</script>

<template>
  <div class="done" @pointerdown="keepAlive">
    <div class="done__top">
      <div class="done__ok">✅ 支付完成</div>
      <div v-if="isTest" class="banner banner--warn done__test">
        ⚠️ 这是<b>测试收款</b>（模拟支付），没有真的收到钱，也不计入营业额
      </div>
    </div>

    <div class="done__middle">
      <div class="done__left">
        <div class="done__label">您的取餐号</div>
        <div class="done__no" data-testid="pickup-no">{{ pickup }}</div>
        <!--
          ⚠️ 这里**不能**写「请到取餐口取餐」。
          付完款的这一刻，订单状态是「已接单」——厨房还没开始做。
          原来那句文案等于主动叫顾客马上去拿，现场就会出现「付完钱就领走」。
          正确的说法是：现在还不能取，等小票上变成「可以取了」再去。
        -->
        <div class="done__hint done__hint--wait">还没好，请稍等</div>
        <div class="done__note">
          厨房做好后，<b>手机上的电子小票会变成「可以取了」</b>，那时再凭取餐号到取餐口
        </div>
        <div class="done__order muted">订单号 {{ order?.orderNo }}</div>
      </div>

      <div class="done__qr">
        <div class="done__qr-title">扫码把电子小票存到手机</div>
        <div v-if="qrSvg" class="done__qr-box" data-testid="receipt-qr" v-html="qrSvg" />
        <div v-else class="done__qr-box done__qr-box--fail">二维码没出来，用手机拍下这一屏也行</div>
        <div class="done__qr-note muted">
          手机没电或没带手机？<b>记住这个号码</b>，取餐口核对号码也能取。
        </div>
      </div>
    </div>

    <div class="done__bottom">
      <div class="muted">{{ left }} 秒后自动返回首页（碰一下屏幕会重新计时）</div>
      <button class="btn btn--primary" data-testid="done-home" @click="backHome">完成，返回首页</button>
    </div>
  </div>
</template>

<style scoped>
.done {
  height: 100%;
  padding: 18px 26px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.done__top {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.done__ok {
  font-size: 28px;
  font-weight: 800;
  color: #8ff0bd;
}

.done__test {
  max-width: 760px;
  text-align: center;
}

/* 左边取餐号、右边电子小票二维码：顾客一眼就能同时看到「号码」和「怎么带走」 */
.done__middle {
  flex: 1;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 44px;
}

.done__left {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.done__label {
  color: var(--muted);
  font-size: 22px;
  letter-spacing: 4px;
}

.done__no {
  font-size: 160px;
  font-weight: 900;
  line-height: 1;
  color: #ffd08a;
  font-variant-numeric: tabular-nums;
  letter-spacing: 6px;
}

.done__hint {
  font-size: 26px;
  font-weight: 700;
}

/* 「还没好」用暖色，别和「可以取了」的绿色搞混 */
.done__hint--wait {
  color: #ffd08a;
}

.done__note {
  font-size: 15px;
  color: var(--muted);
  text-align: center;
  max-width: 460px;
  line-height: 1.5;
}

.done__order {
  font-size: 15px;
}

.done__qr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 340px;
}

.done__qr-title {
  font-size: 22px;
  font-weight: 800;
}

.done__qr-box {
  background: #fff;
  border-radius: 16px;
  padding: 10px;
  width: min(30vh, 240px);
  aspect-ratio: 1 / 1;
}

.done__qr-box :deep(svg) {
  width: 100%;
  height: 100%;
  display: block;
}

.done__qr-box--fail {
  background: var(--panel-2);
  border: 2px dashed var(--line);
  color: var(--muted);
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px;
  font-size: 14px;
}

.done__qr-note {
  font-size: 13px;
  text-align: center;
  line-height: 1.5;
}

.done__bottom {
  display: flex;
  align-items: center;
  gap: 20px;
}

@media (max-aspect-ratio: 1/1) {
  .done__middle {
    gap: 20px;
  }

  .done__no {
    font-size: 110px;
  }
}

@media (max-width: 860px) {
  .done {
    padding: 14px;
    gap: 10px;
  }

  .done__ok {
    font-size: 20px;
  }

  /* 手机竖屏：上下排（号码在上、二维码在下） */
  .done__middle {
    flex-direction: column;
    gap: 14px;
    overflow: auto;
  }

  .done__label {
    font-size: 16px;
    letter-spacing: 2px;
  }

  .done__no {
    font-size: clamp(64px, 22vw, 120px);
    letter-spacing: 2px;
  }

  .done__hint {
    font-size: 18px;
  }

  .done__order {
    font-size: 12px;
  }

  .done__qr-title {
    font-size: 17px;
  }

  .done__qr-box {
    width: min(38vw, 200px);
  }

  .done__bottom {
    flex-direction: column-reverse;
    gap: 8px;
    width: 100%;
  }

  .done__bottom .btn {
    width: 100%;
  }
}
</style>
